import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Badge, Box, Button, Collapsible, HStack, Icon, Link, Separator, Spinner, Text, VStack } from '@chakra-ui/react';
import { ButtonLink } from '@src/components/sco/ButtonLink.tsx';
import { gql } from '@src/lib/gql';
import moment from 'moment';
import { ReactNode, useMemo, useState } from 'react';
import { LuChevronDown, LuChevronRight, LuFile } from 'react-icons/lu';
type FounderAdminInsightsProps = {
  founderUserId: string;
};
const FOUNDER_ADMIN_INSIGHTS_QUERY = gql`
  query FounderAdminInsights($userId: String!) {
    userById(userId: $userId) {
      id
      userId
      fullName
      email
      createdAt
      isRegistered
      isFounderCommunityMember
      isFounderGroupsMember
      subscriptions {
        id
        subscriptionId
        isActive
        activatedAt
        state
        monthlyRecurringRevenueInCents
        totalRevenueInCents
        calculatedTotalNetRevenueInCents
        productId
        product {
          id
          name
          productId
          effectivePrices {
            recurring
          }
        }
      }
      directChatThreads(order: [["_lastActiveAt", "desc"]], page: 1, pageSize: 25) {
        id
        items {
          id
          threadId
          title
          lastActiveAt
          commentsAndRepliesCount
          userMemberships(pageSize: 8) {
            id
            items {
              id
              user {
                id
                userId
                fullName
                displayName
                isAdvisor
                pictureUrl
              }
            }
          }
        }
      }
    }
  }
`;
const FOUNDER_ADMIN_INSIGHTS_FILES_QUERY = gql`
  query FounderAdminInsightsFiles($createdByUserId: String!, $pageSize: Int) {
    storedFiles(
      createdByUserId: $createdByUserId
      page: 1
      pageSize: $pageSize
      order: [["createdAt", "desc"]]
    ) {
      id
      items {
        id
        storedFileId
        title
        fullUrl
        createdAt
        mimeType
      }
      pagination {
        itemCount
      }
    }
  }
`;
const formatCents = (cents?: number | null) => {
  if (typeof cents !== 'number' || Number.isNaN(cents)) {
    return '—';
  }
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })}`;
};
const formatDate = (value?: string | null) => {
  if (!value) {
    return '—';
  }
  const m = moment(value);
  return m.isValid() ? m.format('MMM D, YYYY') : '—';
};

/**
 * Admin-only founder rail: subscription/MRR, registration + LTV, recent advisor chats, files.
 */
export const FounderAdminInsights = t0 => {
  const $ = _c(138);
  const {
    founderUserId
  } = t0;
  const [filesOpen, setFilesOpen] = useState(false);
  let t1;
  if ($[0] !== founderUserId) {
    t1 = {
      userId: founderUserId
    };
    $[0] = founderUserId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = !founderUserId;
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      variables: t1,
      skip: t2,
      fetchPolicy: "cache-and-network"
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const profileQuery = useQuery(FOUNDER_ADMIN_INSIGHTS_QUERY, t3);
  let t4;
  if ($[5] !== founderUserId) {
    t4 = {
      createdByUserId: founderUserId,
      pageSize: 50
    };
    $[5] = founderUserId;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = !founderUserId;
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = {
      variables: t4,
      skip: t5,
      fetchPolicy: "cache-and-network"
    };
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const filesQuery = useQuery(FOUNDER_ADMIN_INSIGHTS_FILES_QUERY, t6);
  const user = profileQuery.data?.userById?.userId === founderUserId ? profileQuery.data.userById : null;
  let subscriptions;
  let t7;
  if ($[10] !== user?.subscriptions) {
    subscriptions = (user?.subscriptions || []).filter(Boolean);
    t7 = subscriptions.filter(_temp);
    $[10] = user?.subscriptions;
    $[11] = subscriptions;
    $[12] = t7;
  } else {
    subscriptions = $[11];
    t7 = $[12];
  }
  const activeSubs = t7;
  let t8;
  if ($[13] !== activeSubs) {
    t8 = activeSubs.reduce(_temp2, 0);
    $[13] = activeSubs;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const mrrCents = t8;
  let t9;
  if ($[15] !== subscriptions) {
    t9 = subscriptions.reduce(_temp3, 0);
    $[15] = subscriptions;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const ltvCents = t9;
  let t10;
  if ($[17] !== subscriptions) {
    const dates = subscriptions.map(_temp4).filter(Boolean).map(_temp5).filter(_temp6).sort(_temp7);
    t10 = dates[0]?.toISOString() ?? null;
    $[17] = subscriptions;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const customerSince = t10;
  let t11;
  if ($[19] !== founderUserId || $[20] !== user?.directChatThreads?.items) {
    t11 = () => {
      const threads = (user?.directChatThreads?.items || []).filter(Boolean).filter(_temp8);
      const rows = [];
      for (const thread of threads) {
        const follows = (thread.userMemberships?.items || []).filter(Boolean);
        const advisor = follows.map(_temp9).filter(Boolean).find(u => u.isAdvisor && u.userId !== founderUserId);
        if (!advisor || !thread.threadId) {
          continue;
        }
        rows.push({
          threadId: String(thread.threadId),
          advisorName: advisor.fullName || advisor.displayName || "Advisor",
          lastActiveAt: thread.lastActiveAt
        });
        if (rows.length >= 5) {
          break;
        }
      }
      return rows;
    };
    $[19] = founderUserId;
    $[20] = user?.directChatThreads?.items;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  user?.directChatThreads?.items;
  let t12;
  if ($[22] !== t11) {
    t12 = t11();
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const recentAdvisorChats = t12;
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let T5;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t26;
  let t27;
  let t28;
  let t29;
  let t30;
  let t31;
  let t32;
  let t33;
  let t34;
  let t35;
  if ($[24] !== activeSubs.length || $[25] !== customerSince || $[26] !== filesOpen || $[27] !== filesQuery.data?.storedFiles?.items || $[28] !== filesQuery.data?.storedFiles?.pagination?.itemCount || $[29] !== filesQuery.loading || $[30] !== ltvCents || $[31] !== mrrCents || $[32] !== profileQuery.error || $[33] !== profileQuery.loading || $[34] !== recentAdvisorChats || $[35] !== user) {
    t35 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const files = (filesQuery.data?.storedFiles?.items || []).filter(Boolean);
      const fileCount = filesQuery.data?.storedFiles?.pagination?.itemCount ?? files.length;
      if (profileQuery.loading && !user) {
        let t36;
        if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
          t36 = <HStack justify="center" paddingY={4}><Spinner size="sm" /></HStack>;
          $[65] = t36;
        } else {
          t36 = $[65];
        }
        t35 = t36;
        break bb0;
      }
      if (profileQuery.error || !user) {
        let t36;
        if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
          t36 = <Text color="colorPalette.fg/50" fontSize="xs" textAlign="center">Admin insights unavailable.</Text>;
          $[66] = t36;
        } else {
          t36 = $[66];
        }
        t35 = t36;
        break bb0;
      }
      T5 = VStack;
      t26 = "stretch";
      t27 = "purple.card/25";
      t28 = "1px solid";
      t29 = "purple.solid/30";
      t30 = "lg";
      t31 = 3;
      t32 = 3;
      t33 = "full";
      if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
        t34 = <HStack align="center" justify="space-between"><Text color="purple.fg" fontWeight="semibold" textStyle="label2xs">Admin insights</Text><Badge colorPalette="purple" size="sm" variant="subtle">Admin</Badge></HStack>;
        $[67] = t34;
      } else {
        t34 = $[67];
      }
      T4 = VStack;
      t18 = "stretch";
      t19 = 2;
      if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
        t20 = <Separator borderColor="purple.solid/15" />;
        $[68] = t20;
      } else {
        t20 = $[68];
      }
      if ($[69] !== activeSubs.length) {
        t21 = <InsightRow label="Product status">{activeSubs.length > 0 ? <Badge colorPalette="green" marginTop={1} size="sm" variant="subtle">Active</Badge> : <Badge colorPalette="gray" marginTop={1} size="sm" variant="subtle">Free / inactive</Badge>}</InsightRow>;
        $[69] = activeSubs.length;
        $[70] = t21;
      } else {
        t21 = $[70];
      }
      let t36;
      if ($[71] !== mrrCents) {
        t36 = formatCents(mrrCents);
        $[71] = mrrCents;
        $[72] = t36;
      } else {
        t36 = $[72];
      }
      if ($[73] !== t36) {
        t22 = <InsightRow label="MRR"><Text fontSize="sm" fontWeight="semibold">{t36}</Text></InsightRow>;
        $[73] = t36;
        $[74] = t22;
      } else {
        t22 = $[74];
      }
      let t37;
      if ($[75] !== user.createdAt) {
        t37 = formatDate(user.createdAt);
        $[75] = user.createdAt;
        $[76] = t37;
      } else {
        t37 = $[76];
      }
      if ($[77] !== t37) {
        t23 = <InsightRow label="Registration date"><Text fontSize="sm">{t37}</Text></InsightRow>;
        $[77] = t37;
        $[78] = t23;
      } else {
        t23 = $[78];
      }
      let t38;
      if ($[79] !== ltvCents) {
        t38 = formatCents(ltvCents);
        $[79] = ltvCents;
        $[80] = t38;
      } else {
        t38 = $[80];
      }
      let t39;
      if ($[81] !== t38) {
        t39 = <Text fontSize="sm" fontWeight="semibold">{t38}</Text>;
        $[81] = t38;
        $[82] = t39;
      } else {
        t39 = $[82];
      }
      let t40;
      if ($[83] !== customerSince) {
        t40 = formatDate(customerSince);
        $[83] = customerSince;
        $[84] = t40;
      } else {
        t40 = $[84];
      }
      let t41;
      if ($[85] !== t40) {
        t41 = <Text color="colorPalette.fg/55" fontSize="xs">Customer since {t40}</Text>;
        $[85] = t40;
        $[86] = t41;
      } else {
        t41 = $[86];
      }
      if ($[87] !== t39 || $[88] !== t41) {
        t24 = <InsightRow label="Customer LTV">{t39}{t41}</InsightRow>;
        $[87] = t39;
        $[88] = t41;
        $[89] = t24;
      } else {
        t24 = $[89];
      }
      if ($[90] !== recentAdvisorChats) {
        t25 = <InsightRow label="Last advisors chatted">{recentAdvisorChats.length === 0 ? <Text color="colorPalette.fg/55" fontSize="xs">No recent advisor threads</Text> : <VStack align="stretch" gap={1.5} width="full">{recentAdvisorChats.map(_temp10)}</VStack>}</InsightRow>;
        $[90] = recentAdvisorChats;
        $[91] = t25;
      } else {
        t25 = $[91];
      }
      T3 = Box;
      T2 = Collapsible.Root;
      t15 = filesOpen;
      if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
        t16 = d_0 => setFilesOpen(d_0.open);
        $[92] = t16;
      } else {
        t16 = $[92];
      }
      let t42;
      if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
        t42 = <Icon as={LuFile} boxSize={3.5} color="colorPalette.fg/50" />;
        $[93] = t42;
      } else {
        t42 = $[93];
      }
      const t43 = <HStack gap={1.5}>{t42}<Text color="colorPalette.fg/50" textStyle="label2xs">File uploads ({fileCount})</Text></HStack>;
      const t44 = filesOpen ? LuChevronDown : LuChevronRight;
      let t45;
      if ($[94] !== t44) {
        t45 = <Icon as={t44} boxSize={3.5} />;
        $[94] = t44;
        $[95] = t45;
      } else {
        t45 = $[95];
      }
      if ($[96] !== t43 || $[97] !== t45) {
        t17 = <Collapsible.Trigger asChild={true}><Button height="auto" justifyContent="space-between" paddingX={0} paddingY={1} size="xs" variant="ghost" width="full">{t43}{t45}</Button></Collapsible.Trigger>;
        $[96] = t43;
        $[97] = t45;
        $[98] = t17;
      } else {
        t17 = $[98];
      }
      T1 = Collapsible.Content;
      T0 = Box;
      t13 = 2;
      t14 = filesQuery.loading && files.length === 0 ? <Spinner size="xs" /> : files.length === 0 ? <Text color="colorPalette.fg/50" fontSize="xs">No uploads found</Text> : <VStack align="stretch" gap={1.5} maxHeight="12rem" overflowY="auto">{files.map(_temp11)}</VStack>;
    }
    $[24] = activeSubs.length;
    $[25] = customerSince;
    $[26] = filesOpen;
    $[27] = filesQuery.data?.storedFiles?.items;
    $[28] = filesQuery.data?.storedFiles?.pagination?.itemCount;
    $[29] = filesQuery.loading;
    $[30] = ltvCents;
    $[31] = mrrCents;
    $[32] = profileQuery.error;
    $[33] = profileQuery.loading;
    $[34] = recentAdvisorChats;
    $[35] = user;
    $[36] = T0;
    $[37] = T1;
    $[38] = T2;
    $[39] = T3;
    $[40] = T4;
    $[41] = T5;
    $[42] = t13;
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
    $[46] = t17;
    $[47] = t18;
    $[48] = t19;
    $[49] = t20;
    $[50] = t21;
    $[51] = t22;
    $[52] = t23;
    $[53] = t24;
    $[54] = t25;
    $[55] = t26;
    $[56] = t27;
    $[57] = t28;
    $[58] = t29;
    $[59] = t30;
    $[60] = t31;
    $[61] = t32;
    $[62] = t33;
    $[63] = t34;
    $[64] = t35;
  } else {
    T0 = $[36];
    T1 = $[37];
    T2 = $[38];
    T3 = $[39];
    T4 = $[40];
    T5 = $[41];
    t13 = $[42];
    t14 = $[43];
    t15 = $[44];
    t16 = $[45];
    t17 = $[46];
    t18 = $[47];
    t19 = $[48];
    t20 = $[49];
    t21 = $[50];
    t22 = $[51];
    t23 = $[52];
    t24 = $[53];
    t25 = $[54];
    t26 = $[55];
    t27 = $[56];
    t28 = $[57];
    t29 = $[58];
    t30 = $[59];
    t31 = $[60];
    t32 = $[61];
    t33 = $[62];
    t34 = $[63];
    t35 = $[64];
  }
  if (t35 !== Symbol.for("react.early_return_sentinel")) {
    return t35;
  }
  let t36;
  if ($[99] !== T0 || $[100] !== t13 || $[101] !== t14) {
    t36 = <T0 paddingTop={t13}>{t14}</T0>;
    $[99] = T0;
    $[100] = t13;
    $[101] = t14;
    $[102] = t36;
  } else {
    t36 = $[102];
  }
  let t37;
  if ($[103] !== T1 || $[104] !== t36) {
    t37 = <T1>{t36}</T1>;
    $[103] = T1;
    $[104] = t36;
    $[105] = t37;
  } else {
    t37 = $[105];
  }
  let t38;
  if ($[106] !== T2 || $[107] !== t15 || $[108] !== t16 || $[109] !== t17 || $[110] !== t37) {
    t38 = <T2 open={t15} onOpenChange={t16}>{t17}{t37}</T2>;
    $[106] = T2;
    $[107] = t15;
    $[108] = t16;
    $[109] = t17;
    $[110] = t37;
    $[111] = t38;
  } else {
    t38 = $[111];
  }
  let t39;
  if ($[112] !== T3 || $[113] !== t38) {
    t39 = <T3>{t38}</T3>;
    $[112] = T3;
    $[113] = t38;
    $[114] = t39;
  } else {
    t39 = $[114];
  }
  let t40;
  if ($[115] !== T4 || $[116] !== t18 || $[117] !== t19 || $[118] !== t20 || $[119] !== t21 || $[120] !== t22 || $[121] !== t23 || $[122] !== t24 || $[123] !== t25 || $[124] !== t39) {
    t40 = <T4 align={t18} gap={t19} separator={t20}>{t21}{t22}{t23}{t24}{t25}{t39}</T4>;
    $[115] = T4;
    $[116] = t18;
    $[117] = t19;
    $[118] = t20;
    $[119] = t21;
    $[120] = t22;
    $[121] = t23;
    $[122] = t24;
    $[123] = t25;
    $[124] = t39;
    $[125] = t40;
  } else {
    t40 = $[125];
  }
  let t41;
  if ($[126] !== T5 || $[127] !== t26 || $[128] !== t27 || $[129] !== t28 || $[130] !== t29 || $[131] !== t30 || $[132] !== t31 || $[133] !== t32 || $[134] !== t33 || $[135] !== t34 || $[136] !== t40) {
    t41 = <T5 align={t26} bg={t27} border={t28} borderColor={t29} borderRadius={t30} gap={t31} padding={t32} width={t33}>{t34}{t40}</T5>;
    $[126] = T5;
    $[127] = t26;
    $[128] = t27;
    $[129] = t28;
    $[130] = t29;
    $[131] = t30;
    $[132] = t31;
    $[133] = t32;
    $[134] = t33;
    $[135] = t34;
    $[136] = t40;
    $[137] = t41;
  } else {
    t41 = $[137];
  }
  return t41;
};
const InsightRow = t0 => {
  const $ = _c(5);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <Text color="colorPalette.fg/50" textStyle="label2xs">{label}</Text>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <VStack align="flex-start" gap={0.5} width="full">{t1}{children}</VStack>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
function _temp(s) {
  return !!s.isActive;
}
function _temp2(sum, s_0) {
  const fromField = s_0.monthlyRecurringRevenueInCents;
  if (typeof fromField === "number") {
    return sum + fromField;
  }
  const recurring = s_0.product?.effectivePrices?.recurring;
  if (typeof recurring === "number") {
    return sum + recurring;
  }
  return sum;
}
function _temp3(sum_0, s_1) {
  const net = s_1.calculatedTotalNetRevenueInCents;
  if (typeof net === "number") {
    return sum_0 + net;
  }
  const total = s_1.totalRevenueInCents;
  if (typeof total === "number") {
    return sum_0 + total;
  }
  return sum_0;
}
function _temp4(s_2) {
  return s_2.activatedAt;
}
function _temp5(d) {
  return moment(d);
}
function _temp6(m) {
  return m.isValid();
}
function _temp7(a, b) {
  return a.valueOf() - b.valueOf();
}
function _temp8(t) {
  return t.commentsAndRepliesCount > 0;
}
function _temp9(f) {
  return f.user;
}
function _temp10(chat) {
  return <HStack key={chat.threadId} align="start" bgColor="colorPalette.contrast/5" gap={2} justify="space-between" padding={2}><VStack align="start" flex={1} gap={0} minWidth={0}><Text fontSize="sm" lineClamp={1}>{chat.advisorName}</Text><Text color="colorPalette.fg/50" fontSize="2xs">{chat.lastActiveAt ? moment(chat.lastActiveAt).fromNow() : ""}</Text></VStack><ButtonLink flexShrink={0} href={`/inbox?threadId=${encodeURIComponent(chat.threadId)}`} rel="noopener noreferrer" size="xs" target="_blank">View</ButtonLink></HStack>;
}
function _temp11(file) {
  return <Link key={file.storedFileId || file.id} color="purple.fg" fontSize="xs" href={file.fullUrl || "#"} lineClamp={1} rel="noopener noreferrer" target="_blank">{file.title || file.storedFileId || "File"}</Link>;
}